import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import * as ReactDatePicker from 'react-datepicker'

import useLocalStorage from 'hooks/use-local-storage'
import useAppSetting from 'hooks/use-app-setting'
import useLanguageQuery from 'queries/use-language'
import { LANGUAGE } from 'utils/constants/locale'
import { shouldShowTranslationKeys, TRANSLATION_KEYS_LANGUAGE } from 'utils/translation-keys'
import { Toast } from 'components'

const useLanguage = () => {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const {
    key: { DEFAULT_LANGUAGE, LANGUAGE_PREFERENCE },
  } = useAppSetting()
  const [language, setLanguage] = useLocalStorage(
    'LANGUAGE_SELECTION',
    LANGUAGE_PREFERENCE ?? DEFAULT_LANGUAGE ?? LANGUAGE.en,
    false,
  )

  const { languageOptions, updateLanguage } = useLanguageQuery()

  const handleUpdate = async (selectedLanguage: string) => {
    const response = await updateLanguage.mutateAsync({ language: selectedLanguage })

    if (response.isSuccess) {
      setLanguage(selectedLanguage)
      Toast.toastMessage({ actionKey: 'update', subject: 'user_language_preference', variant: 'success' })
    } else {
      Toast.toastMessage({ actionKey: 'update', subject: 'user_language_preference', variant: 'error' })
    }

    return response
  }

  const updateTranslation = async (lang: string) => {
    // Backend responses vary by App-Locale, which callAPI reads from the LANGUAGE_SELECTION
    // entry changeLanguage writes — so cached server data is dropped only once that has landed.
    const hasLanguageChanged = i18n.language !== lang

    document.documentElement.lang = lang
    // `?translationKeys=1` swaps the UI to i18next's key-echoing pseudo-language while the stored
    // language, the App-Locale header and the date-picker locale all stay on the real one.
    await i18n.changeLanguage(shouldShowTranslationKeys() ? TRANSLATION_KEYS_LANGUAGE : lang)
    ReactDatePicker.setDefaultLocale(lang)

    if (hasLanguageChanged) {
      queryClient.invalidateQueries()
    }
  }

  useEffect(() => {
    updateTranslation(language)
  }, [language]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    language,
    updateLanguage: handleUpdate,
    languageOptions,
    setLanguage,
  }
}

export default useLanguage
